import type { InventoryItem } from '../types/inventory';
import {
  barcodeToPngDataUrl,
  LABEL_BARCODE_SVG_MAX_HEIGHT_MM,
  LABEL_BARCODE_PRINT_JS_OPTIONS,
} from './inventoryLabelBarcode';
import { buildInventoryLabelsEpl } from './inventoryLabelEpl';
import {
  LABEL_BARCODE_LEFT_MM,
  LABEL_BARCODE_WIDTH_MM,
  LABEL_FOLD_MM,
  LABEL_INFO_LEFT_MM,
  LABEL_INFO_WIDTH_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_PRINT_WIDTH_MM,
} from './inventoryLabelGeometry';
import { labelTextToPngDataUrl } from './inventoryLabelTextPng';
import { printRawEplSilent, type LabelPrintResult } from './labelPrinter';
import { escapeHtml, openPrintWindow, PRINT_POPUP_BLOCKED_MESSAGE } from './printWindow';

export {
  LABEL_PRINT_WIDTH_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_PRINT_GAP_MM,
  LABEL_FOLD_MM,
  LABEL_EMPTY_MM,
  LABEL_PANEL_MM,
  LABEL_INFO_LEFT_MM,
  LABEL_BARCODE_LEFT_MM,
  LABEL_INFO_WIDTH_MM,
  LABEL_BARCODE_WIDTH_MM,
} from './inventoryLabelGeometry';

export interface InventoryLabelPrintRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
}

function formatPriceBs(price: number): string {
  const n = Number(price);
  const s = n.toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `Bs. ${s}`;
}

function formatWeight(weightGrams: number | null): string {
  if (weightGrams == null || Number.isNaN(Number(weightGrams))) return '—';
  return Number(weightGrams).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function pngImg(dataUrl: string, className: string): string {
  if (dataUrl === '') return '';
  return `<img class="${className}" src="${dataUrl}" alt="" />`;
}

/** Prefer silent EPL; fall back to system print dialog (Windows Zebra driver). */
export async function printInventoryLabels(
  rows: InventoryLabelPrintRow[],
): Promise<LabelPrintResult> {
  if (rows.length === 0) {
    return { ok: false, message: 'No hay etiquetas seleccionadas.' };
  }
  const epl = buildInventoryLabelsEpl(rows);
  const silent = await printRawEplSilent(epl);
  if (silent.ok) return silent;

  openInventoryLabelsPrintWindow(rows, true);
  return { ok: true, channel: 'system-dialog' };
}

/**
 * Chrome → ZDesigner label:
 * - code PNG with thick slash (thermal-safe)
 * - Plata/Bs as normal CSS text (cleaner than PNG)
 * - barcode PNG
 */
export function openInventoryLabelsPrintWindow(
  rows: InventoryLabelPrintRow[],
  autoPrint = false,
): void {
  if (rows.length === 0) return;

  const labelsHtml = rows
    .map((row) => {
      const barPng = barcodeToPngDataUrl(row.productCodeUnique, LABEL_BARCODE_PRINT_JS_OPTIONS);
      const codePng = labelTextToPngDataUrl(row.productCodeUnique, {
        fontSizePx: 17,
        fontWeight: 'normal',
        underline: false,
        thickenSlash: false,
        scale: 5,
      });
      const weightPng = labelTextToPngDataUrl(formatWeight(row.weightGrams), {
        fontSizePx: 13,
        fontWeight: 'normal',
        scale: 4,
      });

      return `<div class="label-page">
  <div class="panel panel-barcode">
    <div class="meta">
      ${pngImg(codePng, 'txt-code')}
      ${pngImg(weightPng, 'txt-weight')}
    </div>
    <div class="bar-slot">${pngImg(barPng, 'bar-img')}</div>
  </div>
  <div class="panel panel-info">
    <div class="mat">${escapeHtml(row.materialLabel)}</div>
    <div class="price">${escapeHtml(formatPriceBs(row.salePrice))}</div>
  </div>
</div>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>&nbsp;</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @page {
      size: ${LABEL_PRINT_WIDTH_MM}mm ${LABEL_PRINT_HEIGHT_MM}mm;
      margin: 0;
    }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: ${LABEL_PRINT_WIDTH_MM}mm;
      font-family: Arial, Helvetica, sans-serif;
      background: #fff;
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .label-page {
      position: relative;
      width: ${LABEL_PRINT_WIDTH_MM}mm;
      height: ${LABEL_PRINT_HEIGHT_MM}mm;
      overflow: hidden;
      page-break-after: always;
      break-after: page;
    }
    .label-page:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .panel-barcode {
      position: absolute;
      left: ${LABEL_BARCODE_LEFT_MM}mm;
      top: 0.5mm;
      bottom: 0.5mm;
      width: ${LABEL_BARCODE_WIDTH_MM}mm;
      padding: 0 0.6mm;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0.35mm;
      overflow: hidden;
    }
    .meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      gap: 0.5mm;
      overflow: hidden;
    }
    .txt-code, .txt-weight, .bar-img {
      display: block;
      width: auto;
      max-width: 100%;
    }
    .txt-code {
      height: 2.8mm;
    }
    .txt-weight {
      height: 2.3mm;
    }
    .bar-slot {
      width: 100%;
      height: ${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm;
      display: flex;
      align-items: center;
      overflow: hidden;
    }
    .bar-img {
      height: ${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm;
      image-rendering: pixelated;
      image-rendering: crisp-edges;
    }
    .panel-info {
      position: absolute;
      left: ${LABEL_INFO_LEFT_MM}mm;
      top: 0.5mm;
      bottom: 0.5mm;
      width: ${LABEL_INFO_WIDTH_MM}mm;
      padding: 0 0.8mm;
      display: flex;
      flex-direction: column;
      justify-content: center;
      overflow: hidden;
    }
    .mat {
      font-family: 'Times New Roman', Times, serif;
      font-style: italic;
      font-weight: bold;
      font-size: 9pt;
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .price {
      font-family: 'Times New Roman', Times, serif;
      font-weight: bold;
      font-size: 9pt;
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
    }
    @media screen {
      body {
        width: ${LABEL_PRINT_WIDTH_MM}mm;
        margin: 8px auto;
        background: #e5e7eb;
      }
      .label-page {
        margin-bottom: 8px;
        background: #fff;
        outline: 1px solid #94a3b8;
      }
      .label-page::before {
        content: '';
        position: absolute;
        left: ${LABEL_FOLD_MM}mm;
        top: 0; bottom: 0;
        border-left: 1px dotted #94a3b8;
        z-index: 2;
        pointer-events: none;
      }
      .label-page::after {
        content: '';
        position: absolute;
        left: ${LABEL_INFO_LEFT_MM}mm;
        top: 0; bottom: 0;
        border-left: 1px dashed #64748b;
        z-index: 2;
        pointer-events: none;
      }
    }
  </style>
</head>
<body>${labelsHtml}</body>
</html>`;

  const printWindow = openPrintWindow({
    html,
    size: 'labels',
    autoPrint,
    autoPrintDelayMs: 500,
    windowName: 'printLabels',
  });

  if (!printWindow) {
    window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
  }
}

/** Una etiqueta desde fila de almacén/sucursal. */
export async function printInventoryUnitLabel(item: InventoryItem): Promise<LabelPrintResult> {
  return printInventoryLabels([
    {
      productCodeUnique: String(item.id),
      materialLabel: item.material || '—',
      salePrice: item.price ?? 0,
      weightGrams: item.weightGrams ?? null,
    },
  ]);
}
